import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Circle,
  Upload,
  History,
  Download,
  Delete,
  Visibility,
  CloudUpload,
} from '@mui/icons-material';
import { applications } from '../../utils/variables';
import { ProjectFileUploadResponse } from '../../types/projects';
import { formClient } from '../../utils/httpClientTs';
import PCE_Icon from '../../assets/PCE_Icon+Type_CMYK.png';
import Image from 'mui-image';
import dayjs from 'dayjs';
import { isJuly2026ContentEffective } from '../../utils/july2026ContentEffective';

const PCE_NEW_RECOMMENDATION_URL_LEGACY =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A2B686F6C45384B336C663559353965464D4C462B3552324B3156547445647136412B565668784A35694C313567314754596578436D4D3D';

const PCE_NEW_RECOMMENDATION_URL_JULY_2026 =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A794D30497A78317849704A72714E474931322F3235714D474E36554A675238614F317732565458756A39614D3647705633765357566B3D';

// Test data for file history
const testFileHistory: ProjectFileUploadResponse[] = [
  {
    id: '1',
    name: 'PCE_Recommendation_2024_001.xlsx',
    uploaded_at: '2024-01-15T10:30:00Z',
    uploaded_by: 'Jane Smith',
    status: 'Completed',
    number_of_errors: 0,
  },
  {
    id: '2',
    name: 'PCE_Recommendation_2024_002.csv',
    uploaded_at: '2024-01-10T14:22:00Z',
    uploaded_by: 'Mary Johnson',
    status: 'Processing',
    number_of_errors: 2,
  },
  {
    id: '3',
    name: 'PCE_Recommendation_2024_003.xls',
    uploaded_at: '2024-01-05T09:15:00Z',
    uploaded_by: 'Sarah Wilson',
    status: 'Completed',
    number_of_errors: 0,
  },
];

// Mock error data for demonstration
const mockErrors: { [key: string]: string[] } = {
  '2': [
    'Missing signature on page 3',
    'Incomplete financial information in section 2',
  ],
};

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
      color: '#5D285F',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`pce-tabpanel-${index}`}
    aria-labelledby={`pce-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const PCELandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  );

  const pceNewRecommendationUrl = isJuly2026ContentEffective()
    ? PCE_NEW_RECOMMENDATION_URL_JULY_2026
    : PCE_NEW_RECOMMENDATION_URL_LEGACY;

  const canUploadForm = () => {
    const userApplications = localStorage.getItem('apps');
    return userApplications && userApplications.includes(applications.PCE);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const sendDocument = async (fileData) => {
    await formClient.post('/projects/pce/import', fileData);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('project', 'PCE');

      // Call the sendDocument function
      await sendDocument(formData);

      setUploading(false);
      setUploadSuccess(true);
      setSelectedFile(null);

      // Close dialog after showing success
      setTimeout(() => {
        setUploadDialogOpen(false);
        setUploadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploading(false);
      // You could add error state handling here if needed
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Processing':
        return 'warning';
      case 'Error':
        return 'error';
      default:
        return 'default';
    }
  };

  const toggleRowExpansion = (fileId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [fileId]: !prev[fileId],
    }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with PCE Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={PCE_Icon}
            alt="PCE Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          PCE (Program for Continuing Education)
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The P.E.O. Program for Continuing Education (PCE) provides need-based
          grants to women in the United States or Canada whose education has
          been interrupted and who find it necessary to return to school to
          support themselves and/or their families. All applicants are sponsored
          by a local P.E.O. chapter.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="PCE page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
            {canUploadForm() && <Tab label="File Management" />}
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Local Chapter Recommendation Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Local Chapter Recommendation Process"
              sx={{
                background: 'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{
                variant: 'h5',
                fontWeight: 'bold',
                color: '#fff',
              }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Step
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Resources
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>1.</strong> Review the PCE Policy Guidelines and
                      Procedures and charts to determine recommendation
                      submission date and 18-month completion date.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/1b494768bc6b4da6b8c023c0d1765a42/latest"
                      label="PCE Policy Guidelines and Procedures"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/c12347411c4844519858d8514b9e8377/latest"
                      label="PCE Chart to Determine Recommendation Submission Date"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/bc77d11922a042f6bc08442a8b56ef78/latest"
                      label="PCE Chart to Determine 18-month Completion Date"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/2c9168d286324666a8d9a19003743894/latest"
                      label="PCE Guidance for the Local Chapter"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/009db6a94e7f49a5aa4d4d294c240f12/latest"
                      label="PCE Frequently Asked Questions"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>2.</strong> Prepare for the interview by reviewing
                      the interview guidelines. Provide your candidate with the
                      Candidate's PCE Pre-Application Income and Expense Guide
                      and Worksheet ahead of time and request she provide as
                      much information as possible and bring her documents to
                      the interview.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/b546777e9441494486777c8663020d0f/latest"
                      label="Interviewing the PCE Grant Candidate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/96d25a2c84f5488893992765d8ba1398/latest"
                      label="Candidate's PCE Pre-Application Income and Expense Guide"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/4888ad912ffd4b6c904e263334a86964/latest"
                      label="Candidate's PCE Pre-Application Income and Expense Guide Worksheet"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>3.</strong> Interview your candidate. Complete the
                      Pre-Application Worksheet during the interview.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ButtonLink
                    href="https://peo-portal.com/library/document/4aa6859952ca4f2dab59f76ff2582ff7/latest"
                    label="Chapter Pre-Application Worksheet"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>4.</strong> After a successful chapter vote,
                      complete the PCE Chapter Recommendation Form.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={2}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/ab55ad17a8114b89996935419aca1692/latest"
                      label="How to Write the Chapter's PCE Recommendation"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>5.</strong> Once the recommendation is reviewed,
                      instructions (including the specific deadline for
                      submitting her completed forms) will be emailed to the
                      applicant. The student shall supply proof of enrollment as
                      part of her application process.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/d71664a51bcb4bd7aecbc711ffe0098e/latest"
                      label="PCE Proof of Enrollment Guidelines"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>6.</strong> If the grant is awarded, your chapter
                      can meet with your candidate in person and present her
                      with a certificate of congratulations and/or your chapter
                      can also use the PCE Recipient Media Kit to share
                      information about your recipient with local media outlets.
                      This kit includes a Photography and Publicity Consent form
                      for your recipient to sign.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid container item xs={12} md={6} spacing={2}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/3e08c3e232b84fa5acee48d1a51a4467/latest"
                      label="Grant Presentation Certificate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/0a025d8243ba44b1a3a6546df8fee464/latest"
                      label="Photography and Publicity Consent Form"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/22755a525cda4b6da915396e1b0543fd/latest"
                      label="News Release Sample"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6} textAlign="center">
                  <Link
                    href={pceNewRecommendationUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Start New PCE Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      sx={{ ml: 1 }}
                      href="https://peo-portal.com/library/77fef27667fc4fb391e52322b710e0ff/view/f4728ea037294bdfae957f6d89b027d0"
                      target="_blank"
                      rel="noopener"
                    >
                      Click here for instructions for starting a new online
                      Recommendation Form.
                    </Link>
                  </Typography>
                </Grid>

                <Grid item xs={6} textAlign="center">
                  <Link
                    href="https://peointernational.communityforce.com"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Continue Existing PCE Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      sx={{ ml: 1 }}
                      href="https://peo-portal.com/library/f4b8177901e94f51ad99f4c618d74445/view/59511f483c444a35a6fd168f7768a560"
                      target="_blank"
                      rel="noopener"
                    >
                      Click here for instructions for continuing an existing
                      online Recommendation Form.
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
          </Card>

          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Eligibility Requirements"
              subheader="A woman is eligible for consideration for a Program for Continuing Education grant if she:"
              sx={{
                background: 'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{
                variant: 'h5',
                fontWeight: 'bold',
                color: '#fff',
              }}
              subheaderTypographyProps={{ variant: 'body1', color: '#fff' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is recommended by a local chapter of the P.E.O. Sisterhood
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is a citizen or legal permanent resident of the United
                      States or Canada
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is enrolled in an accredited certification or degree
                      program from an accredited institution; has not already
                      earned a master's degree or above
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is living and studying in the U.S. or Canada for the
                      entire course of study
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Has had at least 24 consecutive months as a non-student
                      sometime in her adult life
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Has begun her educational program, if the course of study
                      is more than one term
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is within 18 consecutive months of completing her
                      educational program
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is enrolled in an education program which, once concluded,
                      will lead to employment or job advancement
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Meets the required legal definition of a woman as
                      specified in Iowa Code Chapter 4.1A1b and 4.1A1c, “an
                      individual who has, had, will have through the course of
                      normal development, or would have but for a developmental
                      anomaly, genetic anomaly, or accident, a reproductive
                      system that at some point produces ova,…”
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', p: 1.5, color: '#2AD2C9' }}
                  >
                    Please Note:
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#2AD2C9',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      A PCE grant is not to be used for living expenses or to
                      repay educational loans or personal debt.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#2AD2C9',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      The grant cannot be used in prerequisite courses needed to
                      secure admission into another program of study.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#2AD2C9',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      A student who is choosing immediate continuation of her
                      education beyond the current program for which the grant
                      is requested is not eligible
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#2AD2C9',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      A student who has already earned a master's degree or
                      above is not eligible.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#2AD2C9',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      A student who is enrolled in a post-master's certificate
                      or doctoral program is not eligible.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Details About the Grant */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Details About the Grant"
              sx={{
                background: 'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ p: 1.5, lineHeight: 1.6, fontSize: '1.1rem' }}
                  >
                    PCE provides one-time grants with a maximum award of $4,000.
                    Grants may be awarded for academic or technical courses,
                    including online programs offered by educational
                    institutions in the United States or Canada.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ p: 1.5, fontWeight: 'bold', color: '#5D285F' }}
                  >
                    The grant can cover education related expenses such as:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Tuition
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Books, supplies
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Testing and graduation fees
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Transportation (only for estimated cost of gas, parking
                      and local bus fare, not for auto loan payments or
                      maintenance)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Childcare (only while in class or studying)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Uniforms required by the educational program
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#5D285F',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Equipment/tools necessary for the course of study
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Become a Brighter Tomorrow Chapter"
              sx={{
                background: 'linear-gradient(135deg, #5D285F 0%, #2AD2C9 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Local chapters can play an active role in empowering women
                      and helping their dreams come true. By making a lump-sum
                      contribution of $500 or more, a chapter can proudly bear
                      the title of a “PCE Brighter Tomorrow Chapter.” This
                      recognition not only highlights your chapter’s commitment
                      to empowering women, but also showcases your dedication to
                      fostering change in the community.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      To become a Brighter Tomorrow Chapter, make sure your
                      chapter's contribution reaches your state, provincial or
                      district (s/p/d) treasurer or executive assistant by
                      January 31. (Chapters in Hawaii should send their
                      contributions to the International Chapter Treasurer).
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Individuals can also be champions of a brighter tomorrow.
                      Lump-sum donations of $500 or more qualify your chapter as
                      a Brighter Tomorrow Chapter. Specify your chapter's name
                      on the Individual Donation Form to create this legacy for
                      your chapter. To be considered a charitable donation, this
                      contribution must be made payable to P.E.O. Program for
                      Continuing Education and be sent to the P.E.O. Executive
                      Office by January 31.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      S/P/D chapter gift transmittals that include donations
                      qualifying for the Brighter Tomorrow program must be
                      received at the P.E.O. Executive Office in Des Moines no
                      later than February 28/29 to be recognized at the upcoming
                      s/p/d convention. Gifts received by the executive office
                      after February 28/29 will be processed and recognized at
                      the following year’s convention.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* File Management Tab */}
        {canUploadForm() && (
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              {/* Upload Section */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                  <CardHeader
                    title="Upload PCE Files"
                    avatar={<CloudUpload color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Upload PCE recommendation files and supporting documents
                      for processing.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Upload />}
                      onClick={() => setUploadDialogOpen(true)}
                      fullWidth
                      sx={{ py: 1.5 }}
                    >
                      Upload New File
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Statistics Section */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                  <CardHeader
                    title="Upload Statistics"
                    avatar={<History color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="#1976d2"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {testFileHistory.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Files
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="success.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.status === 'Completed'
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="warning.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.status === 'Processing'
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Processing
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="error.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.number_of_errors > 0
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          With Errors
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* File History Table */}
              <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                  <CardHeader
                    title="File History"
                    avatar={<History color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              File Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Uploaded By
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Upload Date
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Errors
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testFileHistory.map((file) => (
                            <React.Fragment key={file.id}>
                              <TableRow hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {file.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{file.uploaded_by}</TableCell>
                                <TableCell>
                                  {formatDate(file.uploaded_at)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={file.status}
                                    color={getStatusColor(file.status) as any}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {file.number_of_errors > 0 ? (
                                    <Chip
                                      label={`${file.number_of_errors} errors`}
                                      color="error"
                                      size="small"
                                    />
                                  ) : (
                                    <Chip
                                      label="No errors"
                                      color="success"
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      title="View Errors"
                                      onClick={() =>
                                        toggleRowExpansion(file.id)
                                      }
                                      disabled={file.number_of_errors === 0}
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      title="Download"
                                    >
                                      <Download fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      title="Delete"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {/* Error Details Row */}
                              {file.number_of_errors > 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ p: 0 }}>
                                    <Collapse
                                      in={expandedRows[file.id]}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Box
                                        sx={{
                                          p: 2,
                                          backgroundColor: '#fff3e0',
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          color="warning.main"
                                          sx={{ mb: 1, fontWeight: 'bold' }}
                                        >
                                          Errors Found:
                                        </Typography>
                                        {mockErrors[file.id]?.map(
                                          (error, index) => (
                                            <Typography
                                              key={index}
                                              variant="body2"
                                              sx={{ mb: 0.5, pl: 2 }}
                                            >
                                              • {error}
                                            </Typography>
                                          )
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload PCE File</DialogTitle>
        <DialogContent>
          {uploadSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              File uploaded successfully!
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a file to upload for PCE processing. Supported formats:
                XLS, XLSX, CSV
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                Note: CSV files should use comma separation and UTF-8 encoding
                for best compatibility.
              </Typography>
              <input
                accept=".xls,.xlsx,.csv"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  fullWidth
                  sx={{ py: 2, borderStyle: 'dashed' }}
                >
                  {selectedFile ? selectedFile.name : 'Choose File'}
                </Button>
              </label>
              {selectedFile && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'primary.main' }}
                >
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            {uploadSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!uploadSuccess && (
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              variant="contained"
              startIcon={uploading ? <LinearProgress /> : <Upload />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PCELandingPage;
